<template>
  <!-- 初期ロードが完了するまでローディング表示 -->
  <div
    v-if="!loaded"
    class="loading-wrapper"
  >
    Loading...
  </div>
  <v-app v-else>
    <div :class="auth.route === 'authenticated' ? 'app-page' : 'login-page'">
      <authenticator
          :form-fields="formFields"
          :hide-sign-up="true"
      >
        <template #sign-in-header>
          <h3
              class="amplify-heading"
              style="padding: var(--amplify-space-xl) 0 0 var(--amplify-space-xl); font-size: larger; font-weight: bold;"
          >
            {{ I18n.get('Sign In') }}
          </h3>
        </template>

        <template #sign-in-footer>
          <div v-if="isLoginTypeEmail" class="hint">
            {{ I18n.get('Forgot Password') }}
            <button
                class="textlink"
                @click="toForgotPassword"
            >
              {{ I18n.get('Reset password') }}
            </button>
          </div>
          <div v-else></div>
        </template>

        <!-- 認証コード入力画面（verifyUser / confirmVerifyUser）のフッター -->
        <!-- スキップボタンを非表示にし、代わりに「ログイン画面へ戻る」ボタンを表示 -->
        <template #verify-user-footer>
          <button
            class="amplify-button amplify-field-group__control amplify-button--link amplify-button--small drf-back-to-login"
            data-fullwidth="false"
            data-variation="link"
            data-size="small"
            type="button"
            style="font-weight: normal"
            @click="onClickBackToLogin"
          >
            {{ I18n.get('Back to Sign In') }}
          </button>
        </template>

        <template #confirm-verify-user-footer>
          <button
            class="amplify-button amplify-field-group__control amplify-button--link amplify-button--small drf-back-to-login"
            data-fullwidth="false"
            data-variation="link"
            data-size="small"
            type="button"
            style="font-weight: normal"
            @click="onClickBackToLogin"
          >
            {{ I18n.get('Back to Sign In') }}
          </button>
        </template>

        <template #confirm-reset-password-footer>
          <div style="text-align: center">
            <button
              class="amplify-button amplify-field-group__control"
              data-fullwidth="false"
              data-size="small"
              data-variation="link"
              type="button"
              style="font-weight: normal"
              @click="toSignIn"
            >
              {{ I18n.get('Back to Sign In') }}
            </button>
          </div>
        </template>

        <template v-if="auth.route === 'authenticated' && appReady">
          <v-navigation-drawer
            v-model="openDrawer"
            position="fixed"
            class="bk_gray_1"
            floating
          >
            <v-list-item>
              <div>
                <v-list-item-title>
                  <v-icon>mdi-account</v-icon>
                  <span v-if="userName && authState === 'authenticated'">
                    {{ userName }}
                  </span>
                  <v-icon>menu-open</v-icon>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span>{{ shopName }}</span>
                </v-list-item-subtitle>
              </div>
            </v-list-item>

            <v-divider class="mx-2" />
            <v-list
              mandatory
            >
              <v-list
                density="compact"
                class="mt-5"
              >
                <v-list-item
                  v-for="(menuItem, index) in menuItems"
                  :key="menuItem.title"
                  :class="index === activeMenuIndex ? 'bk_white color_black' : 'color_gray_menu'"
                  style="cursor: pointer;"
                  @click="onClickMenu(menuItem)"
                >
                  <v-list-item-title class="text-subtitle-1">
                    {{ menuItem.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-list>
          </v-navigation-drawer>

          <v-app-bar
            density="compact"
            elevation="0"
            class="br_b_1 bk_white"
          >
            <v-app-bar-nav-icon
              @click.stop="openDrawer = !openDrawer"
            />
            <v-toolbar-title style="color: #70141b" />
            <v-spacer />
            <v-btn
              variant="text"
              class="logout-button"
              @click="onClickLogout"
            >
              <v-icon>mdi-logout</v-icon>
              {{ I18n.get('Sign Out') }}
            </v-btn>
          </v-app-bar>

          <v-main>
            <EmergencyNews :emergency-news-list="emergencyNewsList" />
            <router-view />
          </v-main>
        </template>

        <!-- setupApp 完了までローディング表示 -->
        <template v-if="auth.route === 'authenticated' && !appReady">
          <v-overlay
            :model-value="true"
            class="d-flex align-center justify-center"
          >
            <AlertDialog
              v-if="setupErrorMessage"
              :model-value="true"
              title="エラー"
              :message="setupErrorMessage || ''"
              type="error"
              ok-text="OK"
              @ok="onClickLogout"
            />
            <v-progress-circular
              v-else
              indeterminate
              size="64"
            ></v-progress-circular>
          </v-overlay>
        </template>
      </authenticator>
    </div>
  </v-app>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  GetShopDocument,
  GetShopQuery,
  GetShopQueryVariables,
  ListDssEmergencyNewsDocument,
  ListDssEmergencyNewsQuery,
  ListDssEmergencyNewsQueryVariables,
} from '@/generated/graphql';
import { routerPath } from '@/common/router';
import '@/assets/style.css';
import { generateApolloClient } from '@/common/apolloClientUtil';
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetMenuPermission,
  localStorageGetShopCode,
  localStorageGetUserName,
  localStorageGetAuthType,
  localStorageGetUserId,
  localStorageSetUserItem,
  localStorageClearAuthCache,
  localStoragePurgeObsoleteIdToken,
} from '@/common/localStorageUtil';
import EmergencyNews from '@/components/EmergencyNews.vue';
import AlertDialog from '@/components/AlertDialog.vue';
import {
  signOut,
  fetchUserAttributes,
  getCurrentUser
} from '@aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import shopStore from '@/store/shopStore';
import chainStore from '@/store/chainStore';
import { phrases as phrasesData, isLoginTypeEmail } from '@/authConfig';
import {
  Authenticator,
  useAuthenticator,
  translations
} from '@aws-amplify/ui-vue';
import "@aws-amplify/ui-vue/styles.css";
import { getChainInfo } from '@/common/graphql/chain';

I18n.putVocabularies(translations);
I18n.putVocabularies(phrasesData);
I18n.setLanguage('ja');

// Amplify UI Authenticatorのフォームフィールド設定
// masterブランチのラベル設定に合わせてプレースホルダーを空白に設定
const formFields = {
  signIn: {
    username: {
      placeholder: ' ',
    },
    password: {
      placeholder: ' ',
    },
  },
  resetPassword: {
    username: {
      placeholder: ' ',
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: ' ',
    },
    password: {
      placeholder: ' ',
    },
    confirm_password: {
      placeholder: ' ',
    },
  },
};

interface MenuItem {
  title: string;
  path: string;
  name: string;
}

// ルーター・ストア
const router = useRouter();
const store = useStore();

// 認証状態
const auth = useAuthenticator();

// パスワードリセット画面へ遷移
function toForgotPassword(): void {
  auth.toForgotPassword();
}

// サインイン画面へ遷移
function toSignIn(): void {
  auth.toSignIn();
}

async function ensureAuthCacheInLocalStorage(): Promise<void> {
  if (localStorageGetUserId()) {
    return;
  }

  await refreshAuthCacheInLocalStorage();
}

/**
 * 認証コード入力画面等からログイン画面へ戻る
 * サインアウトしてからページをリロードしてログイン画面へ遷移する
 * ※ verifyUser/confirmVerifyUser状態ではAmplifyのtoSignIn()が効かないため
 *   signOut後にページリロードで対応する
 */
async function onClickBackToLogin(): Promise<void> {
  try {
    await signOut();
  } catch (e) {
    console.warn('signOut error on back to login', e);
  }
  window.location.reload();
}

// UI 初期化フラグ
const loaded = ref(false);

// 認証状態
const authState = ref<string>('');

// ユーザー名
const userName = ref('');

// 店舗名
const shopName = ref('');

// 権限タイプ
const authType = ref('');

// メニューアイテム
const menuItems = ref<MenuItem[]>([]);

// メニュー横スライド開閉
const openDrawer = ref(false);

// メニュー選択中のインデックス
// メニュークリックまたはログイン後の初期遷移時のみ更新される
// アプリ内のページ遷移（一覧→編集等）では変化しない
const activeMenuIndex = ref(-1);

// 緊急ニュースリスト
const emergencyNewsList = ref<
  | ({
      __typename?: 'DssEmergencyNewsData';
      emergency_news_pk_id: string;
      emergency_news_contents: string;
      news_start_datetime: string;
      news_end_datetime: string;
      update_date: string;
    } | null | undefined)[]
  | null
  | undefined
>([]);

// unsubscribe関数
const unsubscribeAuth = ref<() => void>(() => {
  //
});

// setupApp 二重実行防止（auth change が複数回発火することがあるため）
const setupStarted = ref(false);

// setupApp 完了フラグ（true になるまで認証済みコンテンツを描画しない）
const appReady = ref(false);
const setupErrorMessage = ref<string | null>(null);

// ===== アイドルタイムアウト（プラグインに一元化） =====
// IdleTimeout プラグイン（plugins/IdleTimeout/index.ts）が責務を持つ。
// App.vue は start / stop の呼び出しのみ行う。
const idleTimeout = getCurrentInstance()?.appContext.config.globalProperties.$idleTimeout;

/**
 * ログアウト @click
 */
async function onClickLogout(): Promise<void> {
  try {
    await signOut();
  } finally {
    idleTimeout?.stop();
    localStorageClearAuthCache();

    chainStore.clear();
    appReady.value = false;
    setupErrorMessage.value = null;
    menuItems.value = [];
    emergencyNewsList.value = [];
    userName.value = '';
    shopName.value = '';
    authType.value = '';
    setupStarted.value = false;
    activeMenuIndex.value = -1;

    await nextTick();
    if (router.currentRoute.value.path !== routerPath.login) {
      await router.push({ path: routerPath.login });
    }
  }
}

function updateActiveMenuIndexByPath(path: string): void {
  const index = menuItems.value.findIndex((item) => item.path === path);
  if (index !== -1) {
    activeMenuIndex.value = index;
  }
}

/**
 * メニュー @Click
 * @param menuItem
 */
async function onClickMenu(menuItem: MenuItem): Promise<void> {
  // メニュークリック時にアクティブインデックスを更新する
  const clickedIndex = menuItems.value.findIndex((item) => item.path === menuItem.path);
  if (clickedIndex !== -1) {
    activeMenuIndex.value = clickedIndex;
  }
  if (router.currentRoute.value.path !== menuItem.path) {
    await router.push({ path: menuItem.path });
  }
  store.commit('listEditOff');
}

/**
 * Amplify/Cognito が localStorage に userData を保存しない環境でも動くように、
 * Auth API から取得して "userData" を必ず作る（キャッシュ）。
 * ※ 旧実装では ".userData" キーに別形式で保存していたが、"userData" キーに統一。
 * ※ ID トークンはここでは保存しない。取得経路は fetchAuthSession() のみ。
 */
async function refreshAuthCacheInLocalStorage(): Promise<void> {
  try {
    const [user, attrs] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes(),
    ]);

    localStorageClearAuthCache();
    localStorageSetUserItem({
      ...attrs,
      username: user.username,
    });

    console.log('[refreshAuthCacheInLocalStorage] refreshed auth cache', {
      username: user.username,
    });
  } catch (e) {
    console.error('[refreshAuthCacheInLocalStorage] failed', e);
  }
}

async function setupApp(): Promise<void> {
  if (setupStarted.value) {
    return;
  }
  setupStarted.value = true;
  setupErrorMessage.value = null;
  let setupSucceeded = false;
  try {
    console.log('[setupApp] called');

    // まず localStorage(".userData") を必ず作る
    await ensureAuthCacheInLocalStorage();

    console.log('[setupApp] localStorage snapshot', {
      menuPermission: localStorageGetMenuPermission(),
      authType: localStorageGetAuthType(),
      userName: localStorageGetUserName(),
      chain: localStorageGetChainCode(),
      customer: localStorageGetCustomerCode(),
      shop: localStorageGetShopCode(),
      userId: localStorageGetUserId(),
    });

    // 設定可能なすべてのメニュー情報
    const allMenuItems: MenuItem[] = [
      {
        title: '配送依頼登録',
        path: routerPath.deliveryRequestRegisterStart,
        name: 'DeliveryRequestRegisterStart',
      },
      {
        title: '配送依頼一覧',
        path: routerPath.deliveryRequestList,
        name: 'DeliveryRequestList',
      },
      {
        title: '在庫移動・回収依頼登録',
        path: routerPath.deliveryMoveRegister,
        name: 'DeliveryMoveRegister',
      },
      {
        title: '在庫移動・回収依頼一覧',
        path: routerPath.deliveryMoveList,
        name: 'DeliveryMoveList',
      },
      {
        title: '資材発注登録',
        path: routerPath.fixturesRegister,
        name: 'FixturesRegister',
      },
      {
        title: '定期配送',
        path: routerPath.regularDeliveryList,
        name: 'RegularDelivery',
      },
      {
        title: '会員登録',
        path: routerPath.memberRegister,
        name: 'MemberRegister',
      },
      {
        title: '会員一覧',
        path: routerPath.memberList,
        name: 'MemberList',
      },
      {
        title: '店舗登録',
        path: routerPath.shopRegister,
        name: 'ShopRegister',
      },
      {
        title: '店舗一覧',
        path: routerPath.shopList,
        name: 'ShopList',
      },
      {
        title: 'エリア・配達区画設定',
        path: routerPath.areaList,
        name: 'AreaList',
      },
      {
        title: '配達設定',
        path: routerPath.deliverySetting,
        name: 'DeliverySetting',
      },
      {
        title: '臨時キャップ変更',
        path: routerPath.deliveryCapSetting,
        name: 'DeliveryCapSetting',
      },
      {
        title: '店舗間定期配達',
        path: routerPath.scheduledDeliveryList,
        name: 'ScheduledDelivery',
      },
      {
        title: 'チェーン登録',
        path: routerPath.chainRegister,
        name: 'ChainRegister',
      },
      {
        title: 'オペレータ登録',
        path: routerPath.operatorRegister,
        name: 'OperatorRegister',
      },
      {
        title: 'オペレータ一覧',
        path: routerPath.operatorList,
        name: 'OperatorList',
      },
      {
        title: '売上集計',
        path: routerPath.salesSummaryList,
        name: 'SalesSummaryList',
      },
    ];

    // 仮会員登録用QRコードメニュー情報
    const tmpMemberQrCodeMenu: MenuItem = {
      title: '仮会員登録用QRコード',
      path: routerPath.tmpMemberQrCode,
      name: 'TmpMemberQrCode',
    };

    // パスワード変更メニュー情報
    const changePasswordMenu: MenuItem = {
      title: 'パスワード変更',
      path: routerPath.passwordChange,
      name: 'PasswordChange',
    };

    // ログインしたユーザーが使用できるメニュー情報を取得して設定する
    menuItems.value = [];
    const menuNames: string[] | null = localStorageGetMenuPermission();
    if (menuNames !== null) {
      for (let i = 0; i < allMenuItems.length; i++) {
        for (let j = 0; j < menuNames.length; j++) {
          if (allMenuItems[i].name === menuNames[j]) {
            menuItems.value.push(allMenuItems[i]);
          }
        }
      }
    }

    // 権限
    authType.value = localStorageGetAuthType();

    // ユーザ名
    userName.value = localStorageGetUserName();

    // 店舗名
    const apolloClient = await generateApolloClient(true);

    // 認証後遷移画面の出し分け
    if (authType.value === '0') {
      // ======================================
      // 一般権限 （authType: 0）
      // ======================================
      // 配送依頼登録画面へ遷移
      await router.push({
        path: routerPath.deliveryRequestRegisterStart,
      });
      updateActiveMenuIndexByPath(routerPath.deliveryRequestRegisterStart);

      // 店舗情報を取得して店舗名を表示、店舗情報をストアに保存
      const shopCustomerCode = localStorageGetCustomerCode();
      const shopChainCode = localStorageGetChainCode();
      const shopCode = localStorageGetShopCode();
      // 必須パラメータが空の場合は店舗情報取得をスキップ
      if (!shopCustomerCode || !shopChainCode || !shopCode) {
        console.warn('[setupApp] 店舗情報取得に必要なパラメータが不足しています。', {
          customer_code: shopCustomerCode, chain_code: shopChainCode, shop_code: shopCode,
        });
      } else {
      const result: ApolloQueryResult<GetShopQuery> = await apolloClient.query<
        GetShopQuery,
        GetShopQueryVariables
      >({
        query: GetShopDocument,
        variables: {
          customer_code: shopCustomerCode,
          chain_code: shopChainCode,
          shop_code: shopCode,
        },
      });
      if (result.data.getShop !== undefined && result.data.getShop !== null) {
        // 店舗名を取得
        shopName.value = result.data.getShop.name;

        // -----------------------
        // 店舗情報をstoreに保存
        // -----------------------
        // 荷札印刷可能フラグ
        shopStore.updateShippingLabelPrintFlg(
          result.data.getShop.shipping_label_print_flg
        );
        // 来店宅配配達票印刷有効・無効のフラグ
        shopStore.updateDeliveryOrderPrintFlg(
          result.data.getShop.delivery_order_print_flg
        );
        // 仮会員の登録可能フラグ
        shopStore.updateTmpMemberRegistrationFlg(
          result.data.getShop.tmp_member_registration_flg
        );

        if (result.data.getShop.double_deli_oricon_count !== null) {
          shopStore.updateDoubleDeliOriconCount(
            result.data.getShop.double_deli_oricon_count!
          );
        }
        if (result.data.getShop.charter_deli_oricon_count !== null) {
          shopStore.updateCharterDeliOriconCount(
            result.data.getShop.charter_deli_oricon_count!
          );
        }
        let shopAddress = '';
        if (
          result.data.getShop.zip_code !== null &&
          result.data.getShop.zip_code !== undefined
        ) {
          shopAddress =
            '〒' + result.data.getShop.zip_code.substring(0, 3) + '-';
          shopAddress += result.data.getShop.zip_code.substring(3) + ' ';
        }
        if (
          result.data.getShop.address1 !== null &&
          result.data.getShop.address1 !== undefined
        ) {
          shopAddress += result.data.getShop.address1;
        }
        if (
          result.data.getShop.address2 !== null &&
          result.data.getShop.address2 !== undefined
        ) {
          shopAddress += ' ' + result.data.getShop.address2;
        }
        if (
          result.data.getShop.address3 !== null &&
          result.data.getShop.address3 !== undefined
        ) {
          shopAddress += ' ' + result.data.getShop.address3;
        }
        shopStore.updateShopAddress(shopAddress);

        // 店舗で仮会員登録機能フラグがONの場合は
        // =>メニューに仮会員登録用QRコード画面を追加
        if (result.data.getShop.tmp_member_registration_flg === 1) {
          menuItems.value.push(tmpMemberQrCodeMenu);
        }
      }
      } // else: 必須パラメータが空の場合のスキップ終了
    } else if (authType.value === '1') {
      // ======================================
      // 本部権限 （authType: 1）
      // ======================================
      await router.push({
        path: routerPath.deliveryRequestList,
      });
      updateActiveMenuIndexByPath(routerPath.deliveryRequestList);

      // メニューに仮会員登録用QRコード画面を追加
      menuItems.value.push(tmpMemberQrCodeMenu);
    } else if (authType.value === '2') {
      // ======================================
      // 運用管理者 （authType: 2）
      // ======================================
      await router.push({
        path: routerPath.deliverySetting,
      });
      updateActiveMenuIndexByPath(routerPath.deliverySetting);
    } else if (authType.value === '9') {
      // ======================================
      // システム管理者 （authType: 9）
      // ======================================
      await router.push({
        path: routerPath.chainRegister,
      });
      updateActiveMenuIndexByPath(routerPath.chainRegister);
    }

    // すべての権限で共通のメニューとしてパスワード変更を追加
    menuItems.value.push(changePasswordMenu);

    // throw new Error('テスト用エラー');

    // チェーン情報を取得してストアにキャッシュし、アイドルタイムアウト監視を開始
    try {
      const chainInfo = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      // チェーン情報をストアに保存（子コンポーネントから即座に参照可能にする）
      chainStore.setChainInfo(chainInfo);
      const idleLogoutSeconds = chainInfo?.idle_logout_seconds ?? 0;
      idleTimeout?.start(router, idleLogoutSeconds, onClickLogout);
    } catch (err) {
      console.error('[setupApp] Failed to get chain info for idle timeout', err);
      chainStore.setChainInfo(null);
      idleTimeout?.start(router, 0, onClickLogout);
    }
    setupSucceeded = true;
  } catch (err) {
    // セットアップ処理にてエラー
    console.error('[setupApp] error during setup', err);
    setupErrorMessage.value = '認証は正常に完了しましたが、\nその後の処理でエラーが発生しました。\n\n同じユーザーで再度ログインしてください。';
    setupStarted.value = false;
  } finally {
    appReady.value = setupSucceeded;
    console.log('[setupApp] appReady =', appReady.value);
  }
}

// created() 相当の初期処理
(async () => {
  console.log('App created');
  // 移行前のブラウザに残った ID トークンを破棄する
  localStoragePurgeObsoleteIdToken();
  const state = localStorage.getItem('amplifyAuthState');
  if (['verifyUser', 'confirmVerifyUser', 'confirmSignIn', 'confirmSignUp'].includes(state || '')) {
    try {
      await signOut();
    } catch (e) {
      console.warn('Auth.signOut error ignored', e);
    }
    localStorage.removeItem('amplifyAuthState');
  }
  loaded.value = true;
})();

let intervalId: number;

onMounted(() => {
  // 緊急ニュースは30分ごとに定期取得する（取得失敗時もアプリ本体は継続動作）
  // onAuthUIStateChangeはv6系では不要。Amplify UI Vueのslotやイベントで管理する。
  intervalId = window.setInterval(async () => {
    // 未ログイン時は取得しない。ガードが無いと、ログイン画面を開いたまま
    // 放置されたブラウザから 30 分おきに 401 が出続ける
    if (auth.authStatus !== 'authenticated') {
      return;
    }

    const apolloClient = await generateApolloClient();
    try {
      const listNews = await apolloClient.query<
          ListDssEmergencyNewsQuery,
          ListDssEmergencyNewsQueryVariables
      >({
        query: ListDssEmergencyNewsDocument,
        errorPolicy: "all",
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          user_id: localStorageGetUserId(),
        },
      });

      emergencyNewsList.value = [];
      const news = listNews.data.listDssEmergencyNews;
      if (
          news !== null &&
          news !== undefined &&
          news.dss_emergency_news_data !== null &&
          news.dss_emergency_news_data !== undefined &&
          news.dss_emergency_news_data.length > 0
      ) {
        news.dss_emergency_news_data!.forEach(value => {
          if (
              value !== undefined &&
              value !== null &&
              emergencyNewsList.value !== null &&
              emergencyNewsList.value !== undefined
          ) {
            emergencyNewsList.value.push({
              emergency_news_pk_id: value.emergency_news_pk_id,
              emergency_news_contents: value.emergency_news_contents,
              news_start_datetime: value.news_start_datetime,
              news_end_datetime: value.news_end_datetime,
              update_date: value.update_date,
            });
          }
        });
      }
    } catch (err: unknown) {
      console.error('[EmergencyNews] fetch failed', err);
      // throw しない。アプリは動かし続ける
      emergencyNewsList.value = [];
      return;
    }
  }, 30 * 60 * 1000);
});

/**
 * verifyUser / confirmVerifyUser 画面のスキップボタンを非表示にし、
 * フッターのレイアウトを横並びに調整する
 * Amplify UI の内部で描画されるスキップボタンをDOMから検索し、CSSクラスで非表示にする
 */
function hideSkipButtons(): void {
  const authenticator = document.querySelector('[data-amplify-authenticator]');
  if (!authenticator) return;
  // footer内のリンクボタン（スキップボタン）を検索して非表示クラスを付与
  // カスタムの戻るボタン（drf-back-to-login）は除外する
  const linkButtons = authenticator.querySelectorAll(
    'footer > button.amplify-button[data-variation="link"][data-size="small"]:not(.drf-back-to-login)'
  );
  linkButtons.forEach((btn) => {
    btn.classList.add('drf-hide-skip-button');
  });
  // フッターに横並びレイアウト用のクラスを付与
  const footers = authenticator.querySelectorAll('footer.amplify-flex.amplify-authenticator__column');
  footers.forEach((footer) => {
    footer.classList.add('drf-verify-footer');
  });
}

// 認証ルート監視
watch(
() => auth.route,
  async (route) => {
    // Amplify UI の現在ルートをローカル状態へ反映
    authState.value = route;

    // verifyUser / confirmVerifyUser 画面ではスキップボタンを非表示にする
    if (route === 'verifyUser' || route === 'confirmVerifyUser') {
      await nextTick();
      hideSkipButtons();
      // DOMの描画タイミングによっては nextTick 直後に要素が存在しない場合があるため、
      // 少し遅延させて再度実行する
      setTimeout(hideSkipButtons, 100);
      setTimeout(hideSkipButtons, 300);
    }

    // localStorage の更新
    if (route === 'authenticated') {
      // ローカルストレージのamplifyAuthStateを削除
      // =>ローカルの認証状態を初期化
      localStorage.removeItem('amplifyAuthState');
    }
    localStorage.setItem('amplifyAuthState', route);

    if (route === 'authenticated') {
      await refreshAuthCacheInLocalStorage();

      await nextTick();
      await setupApp();
    } else {
      // 未認証へ遷移したら、表示状態・キャッシュ・監視処理を初期化
      localStorageClearAuthCache();
      idleTimeout?.stop();
      chainStore.clear();
      appReady.value = false;
      setupErrorMessage.value = null;
      menuItems.value = [];
      emergencyNewsList.value = [];
      userName.value = '';
      shopName.value = '';
      authType.value = '';
      setupStarted.value = false;
      activeMenuIndex.value = -1;

      // 未認証状態 → ログイン画面へ
      if (router.currentRoute.value.path !== routerPath.login) {
        await router.push({ path: routerPath.login });
      }
    }


    // 緊急ニュースの配列を初期化
    emergencyNewsList.value = [];
  },
  { immediate: true, flush: 'post' }
);

onBeforeUnmount(() => {
  // 定期処理と監視処理を解放
  unsubscribeAuth.value();
  clearInterval(intervalId);
  idleTimeout?.stop();
});
</script>

<style>
.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-size: 1.2rem;
}
.v-data-table td {
  background: #f8f8f8;
}
.v-data-table tr:nth-child(odd) td {
  background: #fff;
}
.v-data-table tr:hover td {
  background-color: #eee;
}
.theme--light.v-input--is-disabled input {
  color: #000 !important;
}

/* パスワードリセット画面のボタン配置をmasterに合わせる */
[data-amplify-authenticator-forgotpassword] .amplify-authenticator__column > footer {
  flex-direction: row !important;
  justify-content: space-between !important;
  align-items: center !important;
}

[data-amplify-authenticator-forgotpassword] .amplify-authenticator__column > footer > .amplify-button--primary {
  order: 2;
  width: auto !important;
  color: #fff !important;
}

[data-amplify-authenticator-forgotpassword] .amplify-authenticator__column > footer > .amplify-button--link {
  order: 1;
  color: #047d95 !important;
}

/* パスワードリセット画面のプレースホルダーを非表示 */
[data-amplify-authenticator-forgotpassword] input::placeholder {
  color: transparent !important;
}

/* 認証コード入力画面（verifyUser / confirmVerifyUser）のスキップボタンを非表示 */
/* スキップボタンはAmplify UIの内部で描画されるため、CSSで非表示にする */
/* drf-hide-skip-button クラスがついたボタンを非表示にする */
button.amplify-button[data-variation="link"][data-size="small"].drf-hide-skip-button {
  display: none !important;
}

/* 認証コード入力画面（verifyUser / confirmVerifyUser）のフッターを横並びレイアウトにする */
footer.drf-verify-footer {
  flex-direction: row !important;
  justify-content: space-between !important;
  align-items: center !important;
}

/* 認証コード入力画面（verifyUser / confirmVerifyUser）の確認/送信ボタンのスタイル */
/* パスワードリセット画面の「認証コードを送信」ボタンサイズに横幅を合わせる */
footer.drf-verify-footer > .amplify-button--primary {
  order: 2;
  width: auto !important;
  min-width: 163px !important;
  color: #fff !important;
  justify-content: center;
}

/* 認証コード入力画面（verifyUser / confirmVerifyUser）の戻るボタンのスタイル */
/* パスワードリセット画面の「ログイン画面に戻る」ボタンデザインに合わせる */
footer.drf-verify-footer > .drf-back-to-login {
  order: 1;
  color: #047d95 !important;
  border-color: transparent !important;
  font-size: 14px !important;
  padding: 6px 12px !important;
}
</style>
